import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement,
  LineElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { BarChart3, Loader2, Info, ArrowUpDown, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchMetaSummary } from '../lib/metaApi';
import { useRegion } from '../hooks/useRegion';
import RegionSwitcher from '../components/meta/RegionSwitcher';
import FormatSwitcher from '../components/meta/FormatSwitcher';
import { buildColorScale, baseChartOptions, HORIZONTAL_BAR_INTERACTION, MAX_SERIES, OTHER_LABEL } from '../constants/metaPalette';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend);

// The table shows the top slice by default; the rest is one click away.
const TABLE_LIMIT = 10;

// Sample tiers replace the raw entry count. They carry the one thing the count
// was load-bearing for — how much weight a rate can take — without publishing a
// number that multiplies back up into the size of the corpus.
const SAMPLE_ORDER = { high: 3, medium: 2, low: 1 };
const SAMPLE_DOTS = { high: 3, medium: 2, low: 1 };

function SampleTier({ tier, t }) {
  if (!tier) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex gap-0.5" aria-hidden="true">
        {[1, 2, 3].map((i) => (
          <span key={i} className={cn('h-1.5 w-1.5 rounded-full',
            i <= SAMPLE_DOTS[tier] ? 'bg-foreground/70' : 'bg-foreground/15')} />
        ))}
      </span>
      <span className="text-xs text-muted-foreground">{t(`meta.sample_${tier}`)}</span>
    </span>
  );
}

/**
 * Sortable columns. `help` explains what each rate is a share OF — the thing
 * readers most often get wrong (these are shares of the archetype's own entries,
 * not of the whole field).
 */
const COLUMNS = [
  { key: 'label', labelKey: 'meta.colArchetype', numeric: false,
    get: (a) => a.label },
  { key: 'sample', labelKey: 'meta.colSample', numeric: true, help: 'meta.helpSample',
    get: (a) => SAMPLE_ORDER[a.sample] ?? 0 },
  { key: 'share', labelKey: 'meta.colShare', numeric: true, help: 'meta.helpShare',
    get: (a) => a.share },
  { key: 'top1', labelKey: 'meta.colTop1', numeric: true, help: 'meta.helpTop1',
    get: (a) => a.top1Rate },
  { key: 'top4', labelKey: 'meta.colTop4', numeric: true, help: 'meta.helpTop4',
    get: (a) => a.top4Rate },
  { key: 'finish', labelKey: 'meta.colFinish', numeric: true, help: 'meta.helpFinish',
    get: (a) => a.meanFinishPercentile },
  { key: 'field', labelKey: 'meta.colField', numeric: true, help: 'meta.helpField',
    get: (a) => a.medianField },
];

const pct = (value, digits = 1) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(digits)}%`;

/** "2026-08-31".."2026-09-06" -> "Aug 31 – Sep 6" (or "Sep 1 – 7" within a month). */
function formatWeek(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const startLabel = start.toLocaleDateString(undefined, opts);
  const endLabel = start.getUTCMonth() === end.getUTCMonth()
    ? end.toLocaleDateString(undefined, { day: 'numeric', timeZone: 'UTC' })
    : end.toLocaleDateString(undefined, opts);
  return `${startLabel} – ${endLabel}`;
}

export default function MetaAnalytics() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { region, setRegion, regions, currentRegion, formats, currentFormat } = useRegion();
  const [searchParams, setSearchParams] = useSearchParams();
  // `?format=` keeps a link to an earlier format shareable; absent = the current one.
  const urlFormat = searchParams.get('format');
  const format = formats.some(f => f.id === urlFormat) ? urlFormat : currentFormat;
  const setFormat = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next && next !== currentFormat) params.set('format', next);
      else params.delete('format');
      return params;
    });
  }, [setSearchParams, currentFormat]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: 'share', dir: 'desc' });
  const [showAll, setShowAll] = useState(false);
  // 0 = every event. Higher cuts to events with at least that many players, because
  // winning a 4-player room is not the same achievement as winning a 40-player one.
  const [minField, setMinField] = useState(0);

  useEffect(() => {
    // Undefined means the region list has not resolved. Fetching now would request
    // the server default and then immediately refetch, flashing the wrong region.
    if (region === undefined) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await fetchMetaSummary({
          region: region || undefined,
          format: format && format !== currentFormat ? format : undefined,
        });
        if (!cancelled) { setData(result); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [region, format, currentFormat]);

  // Whichever event-size cut is selected. Bucket 0 is the full corpus.
  const activeBucket = useMemo(() => {
    if (!data) return null;
    if (!minField) return { minField: 0, archetypes: data.archetypes };
    return (data.sizeBuckets || []).find(b => b.minField === minField) || null;
  }, [data, minField]);

  // Memoised: a fresh [] each render would re-run every downstream useMemo.
  const archetypes = useMemo(() => activeBucket?.archetypes || [], [activeBucket]);

  // Colour is pinned to the archetype label in share order, so re-sorting the
  // table below never repaints the charts. Pinned against the FULL corpus so a
  // deck keeps its colour as you switch event-size cuts.
  const colorFor = useMemo(() => {
    if (!data) return () => '#2a78d6';
    const ranked = [...data.archetypes]
      .filter(a => a.label !== OTHER_LABEL)
      .sort((a, b) => (b.share || 0) - (a.share || 0))
      .map(a => a.label);
    return buildColorScale(ranked);
  }, [data]);

  const topArchetypes = useMemo(
    () => archetypes.filter(a => a.label !== OTHER_LABEL).slice(0, MAX_SERIES),
    [archetypes]
  );

  // Exactly the labels that carry a colour in the charts above.
  const plottedLabels = useMemo(
    () => new Set(topArchetypes.map(a => a.label)),
    [topArchetypes]
  );

  const shareChart = useMemo(() => {
    if (!topArchetypes.length) return null;
    return {
      labels: topArchetypes.map(a => a.label),
      datasets: [{
        label: 'Meta share',
        data: topArchetypes.map(a => a.share),
        backgroundColor: topArchetypes.map(a => colorFor(a.label)),
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 18,
      }],
    };
  }, [topArchetypes, colorFor]);

  const trend = useMemo(() => {
    if (!data?.trend?.weeks?.length) return null;
    const { weeks, weekStarts, weekEnds, droppedWeeks, series } = data.trend;
    // Thin and still-in-progress weeks are already cut server-side, along with
    // the per-week entry counts that used to drive the cut here. All that is
    // left to do is label the axis: "2026-W36" means nothing to a player.
    const axisLabels = weeks.map((_, i) =>
      formatWeek(weekStarts?.[i], weekEnds?.[i]) || weeks[i]);
    return {
      dropped: droppedWeeks || 0,
      chart: {
        labels: axisLabels,
        datasets: series.slice(0, MAX_SERIES).map(s => ({
          label: s.label,
          data: s.shares,
          borderColor: colorFor(s.label),
          backgroundColor: colorFor(s.label),
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.25,
          fill: false,
        })),
      },
    };
  }, [data, colorFor]);

  // Horizontal bars: magnitude by identity, so the measure runs along x.
  const shareOptions = useMemo(() => {
    const base = baseChartOptions();
    return {
      ...base,
      indexAxis: 'y',
      interaction: HORIZONTAL_BAR_INTERACTION,
      plugins: {
        ...base.plugins,
        legend: { display: false }, // the category axis already names every bar
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: {
            label: (ctx) => `${pct(topArchetypes[ctx.dataIndex].share)} of all results`,
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: base.scales.y.grid,
          ticks: { ...base.scales.y.ticks, callback: (v) => `${Math.round(v * 100)}%` },
        },
        y: { grid: { display: false }, ticks: base.scales.x.ticks },
      },
    };
  }, [topArchetypes]);

  const trendOptions = useMemo(() => {
    const base = baseChartOptions({ percentAxis: true });
    return {
      ...base,
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${pct(ctx.parsed.y)}`,
          },
        },
      },
    };
  }, []);

  // Clicking a header sorts by it; clicking the active one flips direction.
  // Text sorts A→Z first, numbers biggest-first, since that's what each is asked for.
  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      const col = COLUMNS.find(c => c.key === key);
      return { key, dir: col?.numeric === false ? 'asc' : 'desc' };
    });
  };

  // "Other" is a residual bucket, not an archetype: it never sorts into the middle
  // of the field, and it is never truncated away either — dropping it would leave
  // the visible shares quietly not adding up.
  const { rankedRows, otherRows } = useMemo(() => {
    if (!archetypes.length) return { rankedRows: [], otherRows: [] };
    const col = COLUMNS.find(c => c.key === sort.key) || COLUMNS[2];
    const factor = sort.dir === 'asc' ? 1 : -1;
    const ranked = archetypes
      .filter(a => a.label !== OTHER_LABEL)
      .sort((a, b) => {
        const av = col.get(a);
        const bv = col.get(b);
        // Missing values sort last in either direction.
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        if (typeof av === 'string') return factor * av.localeCompare(bv);
        return factor * (av - bv);
      });
    return {
      rankedRows: ranked,
      otherRows: archetypes.filter(a => a.label === OTHER_LABEL),
    };
  }, [archetypes, sort]);

  const visibleRows = useMemo(() => [
    ...(showAll ? rankedRows : rankedRows.slice(0, TABLE_LIMIT)),
    ...otherRows,
  ], [rankedRows, otherRows, showAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <BarChart3 className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
        <h1 className="text-xl font-semibold mb-2">
          {t('meta.unavailableTitle') || 'Meta analytics are unavailable'}
        </h1>
        <p className="text-muted-foreground">{error || 'No rollup has been published yet.'}</p>
      </div>
    );
  }

  const generated = data.generatedAt ? new Date(data.generatedAt).toLocaleDateString() : null;

  // Whether to warn that this region's table is thin. Keyed off the coarse size
  // tier from the region index, NOT a corpus count — the API deliberately does not
  // publish one, and the caption must not reconstruct it. In a "small" region most
  // archetypes fall under the rate-reporting floor, so their rates come back null
  // and the table would otherwise just look broken.
  const isSmallSample = (currentRegion?.formatSizes?.[data.format?.id] ?? currentRegion?.size) === 'small';
  // A format that has just started: either no results yet, or results whose
  // archetypes all sit under the reporting floor (they arrive folded into "Other",
  // which the charts leave out — the page would read as blank). Say which, instead
  // of drawing empty charts, and point at the decks and the format that just ended.
  const hasResults = (data.archetypes || []).length > 0;
  const hasRankable = (data.archetypes || []).some(a => a.label !== OTHER_LABEL);
  const isNewFormat = Boolean(data.format?.current) && !hasRankable;
  const isEarlyFormat = isNewFormat && hasResults;
  const previousFormat = [...formats].filter(f => !f.current)
    .sort((a, b) => (b.start || '').localeCompare(a.start || ''))[0] || null;
  // Small because the format is new, not because the circuit is: the region was
  // bigger in the previous format, so "a smaller circuit than Japan" would be wrong.
  const isYoungFormat = Boolean(data.format?.current) && previousFormat
    && (currentRegion?.formatSizes?.[previousFormat.id] || 'small') !== 'small';
  const formatStart = data.format?.start
    ? new Date(`${data.format.start}T00:00:00`).toLocaleDateString(undefined, { dateStyle: 'long' })
    : null;
  const translatedRegion = t(`regions.${data.region}`);
  const regionName = translatedRegion === `regions.${data.region}` ? data.region : translatedRegion;

  return (
    <div className="min-h-[calc(100vh-48px-40px)] bg-gradient-to-b from-background to-muted/30">
      <Helmet>
        <title>Meta Analytics | Ultraman Card Game Tools</title>
        <meta
          name="description"
          content="Tournament metagame analytics for the Ultraman Card Game: archetype share, win rates, trends over time and most-played cards."
        />
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-8 w-8" />
              <h1 className="text-3xl font-bold">{t('meta.title') || 'Meta Analytics'}</h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <FormatSwitcher format={format} formats={formats} onChange={setFormat} />
              <RegionSwitcher
                region={region}
                regions={regions}
                onChange={setRegion}
                variant="dark"
              />
            </div>
          </div>
          <p className="text-white/80 max-w-2xl">
            {t('meta.subtitle') ||
              'What is actually winning tournaments — archetype share, placement rates, how the field has moved week to week, and the cards behind it.'}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-10">
        {/* Scope and provenance. Deliberately no corpus counts: an event or
            result total says nothing a reader acts on, and stating one alongside
            a share hands over the size of the underlying dataset. */}
        <div className="flex gap-3 rounded-lg border bg-muted/40 p-4 text-sm">
          <Info className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
          <div className="space-y-1">
            <p>
              {t('meta.provenance', { region: regionName })}
              {generated && <> · {t('meta.updated', { date: generated })}</>}
            </p>
            {data.format && formatStart && (
              <p>{t('meta.formatScope', { format: data.format.id, date: formatStart })}</p>
            )}
            <p className="text-muted-foreground">
              {t('meta.resultDefinition')} {t('meta.coverageCaveat')}
            </p>
            {isSmallSample && !isNewFormat && (
              // Explains the blank rate columns before the reader decides the page
              // is broken: in a thin region most archetypes sit under the floor, so
              // they ship a share and no rates.
              <p className="text-muted-foreground">
                {isYoungFormat
                  ? t('meta.youngFormatCaveat', { format: data.format.id })
                  : t('meta.smallSampleCaveat', { region: regionName })}
              </p>
            )}
          </div>
        </div>

        {isNewFormat && (
          <Card>
            <CardContent className="p-8 text-center space-y-3">
              <Sparkles className="h-8 w-8 mx-auto text-brand" aria-hidden="true" />
              <h2 className="text-xl font-semibold">
                {t(isEarlyFormat ? 'meta.earlyFormatTitle' : 'meta.newFormatTitle', { format: data.format.id })}
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                {t(isEarlyFormat ? 'meta.earlyFormatBody' : 'meta.newFormatBody',
                  { format: data.format.id, date: formatStart || data.format.start })}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {isEarlyFormat && (
                  <Button
                    onClick={() => navigate(`/decks/tournament${region ? `?region=${encodeURIComponent(region)}` : ''}`)}
                  >
                    {t('meta.browseFormatDecks', { format: data.format.id })}
                  </Button>
                )}
                {previousFormat && (
                  <Button variant="outline" onClick={() => setFormat(previousFormat.id)}>
                    {t('meta.viewPreviousFormat', { format: previousFormat.id })}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {!isNewFormat && (<>
        {/* Meta share */}
        <section>
          <h2 className="text-xl font-semibold mb-1">{t('meta.shareTitle') || 'Meta share'}</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {t('meta.shareSubtitle')}
          </p>
          <Card>
            <CardContent className="p-4">
              <div style={{ height: Math.max(240, topArchetypes.length * 34) }}>
                {shareChart && <Bar data={shareChart} options={shareOptions} />}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Trend */}
        {trend && (
          <section>
            <h2 className="text-xl font-semibold mb-1">{t('meta.trendTitle')}</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('meta.trendSubtitle')}
              {trend.dropped > 0 && ` ${t('meta.trendOmitted', { count: trend.dropped })}`}
            </p>
            <Card>
              <CardContent className="p-4">
                <div style={{ height: 340 }}>
                  <Line data={trend.chart} options={trendOptions} />
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Archetype table — also the relief for the low-contrast chart hues */}
        <section>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="text-xl font-semibold mb-1">{t('meta.tableTitle')}</h2>
              <p className="text-sm text-muted-foreground">{t('meta.tableSubtitle')}</p>
            </div>
            {/* Winning a 4-player room is not the achievement that winning a
                40-player one is, so the table can be cut by minimum field size. */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t('meta.eventSize')}
              </span>
              <div className="flex gap-1">
                <Button size="sm" variant={minField === 0 ? 'secondary' : 'ghost'}
                  onClick={() => setMinField(0)}>
                  {t('meta.eventSizeAll')}
                </Button>
                {(data.sizeBuckets || []).map((b) => (
                  <Button key={b.minField} size="sm"
                    variant={minField === b.minField ? 'secondary' : 'ghost'}
                    onClick={() => setMinField(b.minField)}>
                    {t('meta.eventSizeMin', { n: b.minField })}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {activeBucket && (
            <p className="text-xs text-muted-foreground mb-3">
              {minField === 0
                ? t('meta.sizeScopeAll')
                : t('meta.sizeScopeMin', { n: minField })}
            </p>
          )}

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  {COLUMNS.map((col) => {
                    const active = sort.key === col.key;
                    const header = (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className={cn(
                          'w-full p-3 flex items-center gap-1 hover:text-foreground transition-colors',
                          col.numeric ? 'justify-end' : 'justify-start',
                          active ? 'text-foreground' : 'text-muted-foreground'
                        )}
                      >
                        {t(col.labelKey)}
                        <ArrowUpDown className={cn('h-3 w-3 shrink-0',
                          active ? 'opacity-100' : 'opacity-30')} />
                      </button>
                    );
                    return (
                      <th key={col.key}
                        className={cn('p-0 font-medium', col.numeric ? 'text-right' : 'text-left')}
                        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      >
                        {/* What each rate is a share OF is the thing readers get
                            wrong, so every column explains itself on hover. */}
                        {col.help ? (
                          <Tooltip>
                            <TooltipTrigger asChild>{header}</TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[260px]">
                              {t(col.help)}
                            </TooltipContent>
                          </Tooltip>
                        ) : header}
                      </th>
                    );
                  })}
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((a) => (
                  <tr key={a.label} className="border-t hover:bg-muted/30">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {/* A swatch means "this row is plotted above". Archetypes past
                            the chart's series limit get none, rather than a grey chip
                            that reads as if they were folded into Other. */}
                        <span className="h-3 w-3 rounded-sm shrink-0"
                          style={{ backgroundColor: plottedLabels.has(a.label)
                            ? colorFor(a.label) : 'transparent' }} aria-hidden="true" />
                        <span className="font-medium">{a.label}</span>
                      </div>
                      {a.note && <div className="text-xs text-muted-foreground pl-5">{a.note}</div>}
                    </td>
                    <td className="p-3 text-right"><SampleTier tier={a.sample} t={t} /></td>
                    <td className="p-3 text-right tabular-nums">{pct(a.share)}</td>
                    <td className="p-3 text-right tabular-nums">{pct(a.top1Rate)}</td>
                    <td className="p-3 text-right tabular-nums">{pct(a.top4Rate)}</td>
                    <td className="p-3 text-right tabular-nums">{a.meanFinishPercentile ?? '—'}</td>
                    <td className="p-3 text-right tabular-nums text-muted-foreground">
                      {a.medianField ?? '—'}
                    </td>
                    <td className="p-3 text-right">
                      {a.label !== OTHER_LABEL && (
                        <Button size="sm" variant="ghost"
                          onClick={() => navigate(
                            `/decks/tournament?archetype=${encodeURIComponent(a.label)}`
                            + (region ? `&region=${encodeURIComponent(region)}` : '')
                          )}>
                          {t('meta.viewDecks')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rankedRows.length > TABLE_LIMIT && (
            <div className="mt-3 text-center">
              <Button variant="outline" size="sm" onClick={() => setShowAll(v => !v)}>
                {showAll
                  ? t('meta.showTop', { count: TABLE_LIMIT })
                  : t('meta.showAll', { count: rankedRows.length })}
              </Button>
            </div>
          )}
        </section>

        {/* How the labels are made — the load-bearing methodological choice */}
        <section className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <h2 className="font-semibold text-foreground mb-2">
            {t('meta.methodTitle') || 'How archetypes are labelled'}
          </h2>
          <p>
            {t('meta.methodBody') ||
              'A deck is named after the characters it is actually built on: the pilot’s declared cover character, plus any second character played at four or more copies. Pair names are ordered consistently, so the same deck gets the same name no matter which half its pilot chose for the cover. Cards are matched to the English catalog by card code.'}
          </p>
        </section>
        </>)}
      </div>
    </div>
  );
}
